import React, { useEffect, useState } from "react";
import CryptoJS from "crypto-js";
import { useMaterialUIController } from "../../../context";
import request, { gql } from "graphql-request";
import { useNavigate } from "react-router-dom";
import BASEURL from "../../Utils/Baseurl";
import { useAuth } from "../../../context/AuthContext/AuthContextProvider";
import NoAccessPage from "./noAccessPage";
import { useDispatch, useSelector } from "react-redux";
import { encryptData } from "../../../layoutcontainers/DashboardLayout/components/Navbar/components/Encripted";

const SECRET_KEY = "1234"; // same in App A

const AuthLoader = ({
  text = "CRM",
  colorGradient = ["#8E00b4", "#ad0a87", "#ad0a87", "#8E00b4"], // gradient colors
  size = 48,
  background = "rgba(255,255,255,0.95)",
  duration = 2,
  gap = 8,
  barWidth = 240,
}) => {
  const letters = Array.from(text);
  const navigate = useNavigate();
  const { setAuthenticated, setCurrentUser } = useAuth();
  //   const { updatingUser } = useUserDetails();
  const [controller, dispatch] = useMaterialUIController();

  const [pageState, setPageState] = useState({
    loading: false,
    error: false,
    message: "",
    state: "init",
    animation: false,
  });

  const [user, setUser] = useState({
    name: "",
    userName: "",
    email: "",
    picture: "",
    token: "",
    details: "",
  });

  // Inject keyframes once
  React.useEffect(() => {
    const styleEl = document.createElement("style");
    document.head.appendChild(styleEl);
    const sheet = styleEl.sheet;

    const keyframes = [
      `@keyframes hf-flip {
        0% { transform: perspective(400px) rotateX(0deg) translateY(0); opacity: 1; }
        28% { transform: perspective(400px) rotateX(12deg) translateY(-6%); opacity: 0.95; }
        50% { transform: perspective(400px) rotateX(180deg) translateY(-34%); opacity: 0.5; }
        72% { transform: perspective(400px) rotateX(10deg) translateY(-4%); opacity: 0.95; }
        100% { transform: perspective(400px) rotateX(0deg) translateY(0); opacity: 1; }
        }`,
      `@keyframes hf-message-pulse {
        0% { transform: translateY(0); opacity: 0.9; }
        50% { transform: translateY(-4px); opacity: 1; }
        100% { transform: translateY(0); opacity: 0.9; }
        }`,
      `@keyframes gradient-shift {
        0% { background-position: 0% 50%; }
        100% { background-position: 200% 50%; }
        }`,
    ];

    keyframes.forEach((rule) => sheet.insertRule(rule, sheet.cssRules.length));

    return () => {
      document.head.removeChild(styleEl); // cleanup if needed
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encryptedData = params.get("data");
    // console.log("encryptedData",params);

    if (encryptedData) {
      try {
        const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
        const creds = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));

        // Auto-login using creds directly (avoid async state delay)
        setTimeout(() => {
          handleSignIn(creds.email, creds.password);
        }, 2000);
      } catch (err) {
        console.error("Decryption failed:", err);
      }
    }
  }, []);

  const handleSignIn = async (emailParam, passwordParam) => {
    const LOGIN = gql`
      mutation Login($email: String!, $password: String!) {
        tokenAuth(email: $email, password: $password) {
          success
          errors
          token
          refreshToken
          user {
            id
            email
            username
            firstName
            lastName
            isActive
            role
          }
        }
      }
    `;

    setPageState({
      ...pageState,
      loading: true,
    });

    try {
      const response = await request(BASEURL, LOGIN, {
        email: emailParam,
        password: passwordParam,
      });
      if (response.tokenAuth.success) {
        setAuthenticated(true);
        const user = response.tokenAuth.user;
        localStorage.setItem("userRole", user.role);
        localStorage.setItem("rememberedEmail", encryptData(emailParam));
        localStorage.setItem(
          "rememberedPassword",
          encryptData(passwordParam)
        );
        setCurrentUser(user);
        navigate("/dashboard");
      } else {
        setPageState({
          ...pageState,
          loading: false,
          error: true,
          message: "You don't have access to this page",
          animation: true,
        });
      }
    } catch (error) {
      console.error("error:", error);
      sessionStorage.setItem("token", "undefined");
      sessionStorage.setItem("user", "undefined");

      setPageState({
        ...pageState,
        loading: false,
        error: true,
        title: "Error message",
        message: "An error occurred. Please try again.",
        state: "default",
        animation: true,
      });
    }
  };

  const gradientCSS = `linear-gradient(120deg, ${colorGradient.join(", ")})`;

  return (
    <>
      {pageState.animation ? (
        <>
          <NoAccessPage />
        </>
      ) : (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 99999,
              background,
              backdropFilter: "blur(6px)",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "20px",
                padding: "20px 28px",
                borderRadius: "12px",
              }}
            >
              {/* Letters with gradient */}
              <div style={{ display: "inline-flex", gap: `${gap}px` }}>
                {letters.map((ch, idx) => {
                  const delay = (idx * 0.12).toFixed(2);
                  return (
                    <span
                      key={idx}
                      style={{
                        display: "inline-block",
                        fontWeight: 700,
                        fontSize: `${size}px`,
                        lineHeight: 1,
                        textTransform: "uppercase",
                        backgroundImage: gradientCSS,
                        backgroundSize: "200% 200%",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        color: "transparent",
                        animation: `
                    hf-flip ${duration}s cubic-bezier(.2,.9,.2,1) infinite,
                    gradient-shift 3s linear infinite
                  `,
                        animationDelay: `${delay}s, 0s`,
                      }}
                    >
                      {ch === " " ? "\u00A0" : ch}
                    </span>
                  );
                })}
              </div>

              {/* Slow-moving gradient loader line */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: `${barWidth}px`,
                    height: "8px",
                    borderRadius: "999px",
                    backgroundImage: gradientCSS,
                    backgroundSize: "300% 100%",
                    animation: "gradient-shift 6s ease-in-out infinite",
                    marginBottom: "8px",
                  }}
                />
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "rgba(0,0,0,0.6)",
                    animation: "hf-message-pulse 1.6s ease-in-out infinite",
                  }}
                >
                  Securing your workspace…
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AuthLoader;
